"use client";
import { useEffect, useState } from "react";
import { ArrowLeft } from "react-iconly";
import Image from "next/image";
import { useParams, useRouter } from "next/navigation";

import ForwardIcon from "@/assets/icons/forward-15.svg";
import PauseIcon from "@/assets/icons/pause.svg";
import PlayIcon from "@/assets/icons/play.svg";
import BackwardIcon from "@/assets/icons/replay-5.svg";
import { AudioSlider, ErrorResult } from "@/components/partials";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { AudioInfoType, AudioTypeEnum, useAudioContext } from "@/context";
import { fetchOnClient } from "@/hooks/useFetch/useFetch";
import { MoodType, PaginatedResType } from "@/lib/schema";
import { apiAudio, apiImage } from "@/lib/utils";

export default function MoodDetailClient() {
  const router = useRouter();
  const params = useParams();
  const [data, setData] = useState<PaginatedResType<MoodType[]> | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const fetchParams = {
    url: `/mood/list/${params.id}`,
    options: { searchParams: { limit: 100 } },
    onError: setError,
    onSuccess: setData,
  };

  useEffect(() => {
    setLoading(true);
    fetchOnClient<PaginatedResType<MoodType[]> | null>(fetchParams).finally(
      () => setLoading(false)
    );
  }, []);

  useEffect(() => {
    const isLastStep = step >= (data?.data?.length ?? 0);
    const hasNextPage = !!data?.pagination.nextPage;
    if (isLastStep && hasNextPage) {
      fetchOnClient<PaginatedResType<MoodType[]> | null>({
        ...fetchParams,
        options: {
          ...fetchParams.options,
          searchParams: {
            ...fetchParams.options.searchParams,
            page: data?.pagination.nextPage,
          },
        },
      }).then((res) =>
        setData((d) => ({
          data: [...(d?.data || []), ...(res.data?.data || [])],
          pagination: res.data?.pagination! || {},
        }))
      );
    }
  }, [step]);

  const total = data?.pagination.total ?? data?.data?.length ?? 0;
  const isLast = step + 1 >= total;
  const currentMoodStep = ((data?.data || [])[step] || {}) as MoodType;

  if (error) return <ErrorResult description={error.message} />;
  return (
    <div className="bg-[#FCF4F1] absolute w-full h-full left-0 top-0">
      <Progress
        value={Math.round(((step + 1) / total) * 100)}
        className="w-full fixed top-[65px] left-0 h-0.5 z-10"
      />
      <div className="container py-24 h-full relative">
        <button type="button" onClick={router.back} className="relative">
          <ArrowLeft size={24} />
        </button>
        <div className="sm:max-w-[450px] mx-auto flex flex-col h-full -mt-6">
          <div className="flex-1">
            {currentMoodStep.banner && (
              <figure
                itemProp="image"
                itemScope
                itemType="https://schema.org/ImageObject"
                className="rounded-xl w-[247px] overflow-hidden aspect-square relative mx-auto mb-16 shadow-[0_0_32px_0_#00000029]"
              >
                <Image
                  src={apiImage(currentMoodStep.banner, "medium")}
                  fill
                  alt=""
                  itemProp="url"
                  className="object-cover"
                />
                <meta itemProp="width" content="247" />
                <meta itemProp="height" content="247" />
              </figure>
            )}
            <h1 className="text-center font-bold text-3xl mb-8">
              {currentMoodStep.title}
            </h1>
            <div
              className={currentMoodStep.audio_duration ? "mb-16" : ""}
              dangerouslySetInnerHTML={{ __html: currentMoodStep.body }}
            />

            {!!currentMoodStep.audio_duration && (
              <AudioSection
                id={`${AudioTypeEnum.MOOD}.${currentMoodStep.id}`}
                src={currentMoodStep.audio}
                thumbnail={apiImage(currentMoodStep.banner, "small")}
                title={currentMoodStep.title}
              />
            )}
          </div>
          <div className="flex items-center justify-between">
            {step > 0 ? (
              <Button
                type="button"
                variant="default"
                size="lg"
                className="p-0 aspect-square flex items-center justify-center"
                disabled={loading}
                onClick={() => setStep(Math.max(step - 1, 0))}
              >
                <ArrowLeft size={24} />
              </Button>
            ) : (
              <div />
            )}
            <Button
              type="button"
              variant="default"
              size="lg"
              disabled={loading}
              onClick={() =>
                isLast
                  ? router.replace("/mood")
                  : setStep(Math.min(step + 1, total))
              }
            >
              {isLast ? "Дуусгах" : "Дараах"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AudioSection({ id, src, thumbnail, title }: AudioInfoType) {
  const {
    isPlaying,
    currentTime,
    duration,
    togglePlayPause,
    handleForward,
    handleBackward,
    setAudio,
    formatTime,
    toggleAudioDrawer,
  } = useAudioContext();

  useEffect(() => {
    setAudio(
      {
        id,
        src: apiAudio(src),
        thumbnail,
        title,
      },
      false
    );

    return () => toggleAudioDrawer(true);
  }, []);

  return (
    <>
      <AudioSlider size="lg" />
      <div className="flex justify-between text-sm text-[#898480] mt-1 mb-8">
        <span>{formatTime(currentTime)}</span>
        <span>
          {currentTime >= duration ? "" : "-"}
          {formatTime((duration || 0) - currentTime)}
        </span>
      </div>

      <div className="flex items-center gap-6 justify-center mb-8">
        <button
          type="button"
          className="transition active:-rotate-45 active:scale-95"
          onClick={handleBackward}
        >
          <BackwardIcon style={{ width: 32, height: 32, color: "#84807D" }} />
        </button>
        <button
          type="button"
          className="transition active:scale-75 w-16 h-16 bg-primary flex items-center justify-center rounded-full"
          onClick={togglePlayPause}
        >
          {isPlaying ? (
            <PauseIcon style={{ width: 22, height: 22, color: "#fff" }} />
          ) : (
            <PlayIcon style={{ width: 18, height: 18, color: "#fff" }} />
          )}
        </button>
        <button
          type="button"
          className="transition active:rotate-45 active:scale-95"
          onClick={handleForward}
        >
          <ForwardIcon style={{ width: 32, height: 32, color: "#84807D" }} />
        </button>
      </div>
    </>
  );
}
